// Catalog.ID Client — Signed delegate invitations (§13.5, M15; §14.2, M18)
// ============================================================================
// The owner's device mints a 16-character CODE, derives a lookup id and the
// fixed-value salt from it, and builds and signs the invitation document
// (auth/invitation-document.ts); the node verifies, reserves the delegate
// voucher and stores the row under SHA-256 of the lookup id. The invitee
// re-derives the same lookup id from the code on their card, begins (CID drawn)
// and activates.
//
// The node never receives the code or the salt, and nothing it returns can
// redeem an invitation — there is no secret on this wire at all.

import { api } from './client';

export interface RegisterInvitationRequest {
    /** Canonical (RFC 8785) invitation document, exactly the bytes that were signed. */
    documentJson: string;
    /** Hybrid signature wrapper JSON over "catalog-id invitation v1\n" ‖ documentJson. */
    signature: string;
    /** 64 lowercase hex, `deriveInvitationSecrets(code).lookupIdHex`. The node
     *  stores only its SHA-256; the code it came from stays on this device. */
    lookupId: string;
}

export interface RegisterInvitationResponse {
    invitationId: number;
    expiresAt: string;
    emailFixed: boolean;
    phoneFixed: boolean;
    voucherId: number | null;
}

export type InvitationStatus = 'pending' | 'redeemed' | 'revoked' | 'expired';

export interface InvitationSummary {
    invitationId: number;
    roles: string[];
    emailFixed: boolean;
    phoneFixed: boolean;
    status: InvitationStatus;
    createdAt: string;
    expiresAt: string;
    redeemedAt: string | null;
    revokedAt: string | null;
    delegateCid: string | null;
    inviterCid: string | null;
    voucherId: number | null;
}

export interface BeginInvitationResponse {
    delegateCid: string;
    orgCid: string;
    roles: string[];
    expiresAt: string;
    emailFixed: boolean;
    phoneFixed: boolean;
}

export async function registerInvitation(orgCid: string, req: RegisterInvitationRequest): Promise<RegisterInvitationResponse> {
    return api.post(`/api/organization/${orgCid}/invitations`, req);
}

export async function listInvitations(orgCid: string): Promise<{ invitations: InvitationSummary[] }> {
    return api.get(`/api/organization/${orgCid}/invitations`);
}

export async function revokeInvitation(orgCid: string, invitationId: number): Promise<{ invitationId: number; status: string; voucherReleased: boolean }> {
    return api.del(`/api/organization/${orgCid}/invitations/${invitationId}`);
}

/**
 * Anonymous: draws (idempotently) the delegate CID for a signed invitation,
 * addressed by the lookup id derived from the card's code AND the organization
 * the card names. Sent to the organization's origin (`base`, '' = the entry
 * node): the invitation lives only there, and any other node answers
 * `origin_only` (docs/cid-governance.md M7a). Rate-limited per IP on the node
 * (skipped in Development).
 */
export async function beginInvitation(lookupId: string, orgCid: string, base: string): Promise<BeginInvitationResponse> {
    return api.post('/api/delegate/invitation/begin', { lookupId, orgCid }, { base, anonymous: true });
}

// ── Generalised invitations (§14.3, M19) ────────────────────────────────────
// A PERSONAL or ORGANIZATION invitation has no signed document: the VOUCHER is
// the authority, so whoever holds an unredeemed `RGV-` code can turn it into an
// offer addressed to one person — "pay for a friend in a way that cannot be
// passed on". Anonymous, no session, no signature.
//
// The node still never sees the `INV-` code: this device mints it, derives the
// lookup id and the fixed-value salt from it with one scrypt, and sends only the
// lookup id and the salted digests.

export type InvitationRegistrationType = 'personal' | 'organization' | 'delegate';

export interface CreateInvitationRequest {
    /** `personal` or `organization` — a delegate invitation is owner-signed and
     *  goes through `registerInvitation` instead. */
    registrationType: 'personal' | 'organization';
    /** 64 lowercase hex, `deriveVoucherSecrets(code).lookupIdHex` — the `RGV-`
     *  code being turned into an offer (registration-wallets §4.1, D16). The
     *  CODE never leaves this device: it is what opens the Worker lock holding
     *  the voucher's bits, and a server able to see it could open that lock.
     *  Omitted on a waived node, which sells no vouchers at all. */
    voucherLookupId?: string;
    /** 64 lowercase hex, `deriveInvitationSecrets(code).lookupIdHex`. */
    lookupId: string;
    /** `hashFixedValue(salt, normaliseEmail(...))`. Absent ⇒ not fixed. */
    emailHash?: string;
    /** `hashFixedValue(salt, normalisePhone(...))`. Absent ⇒ not fixed. */
    phoneHash?: string;
    /** Organization invitations only, and in PLAINTEXT — a domain is public, and
     *  the node has to compare it at register/begin, where the domain claim is
     *  still encrypted. */
    domain?: string;
}

export interface CreateInvitationResponse {
    invitationId: number;
    registrationType: 'personal' | 'organization';
    expiresAt: string;
    emailFixed: boolean;
    phoneFixed: boolean;
    domainFixed: boolean;
}

/** What a code turns out to be — the entry page asks before it routes, because
 *  the three registration types are three different wizards. */
export interface InspectInvitationResponse {
    registrationType: InvitationRegistrationType;
    expiresAt: string;
    emailFixed: boolean;
    phoneFixed: boolean;
    domainFixed: boolean;
    /** The fixed domain itself, for the organization wizard's read-only Domain
     *  step. Public by nature; the value HASHES are never returned. */
    domain: string | null;
    orgCid: string | null;
    roles: string[] | null;
}

/**
 * Turn a registration code into an invitation. Anonymous — possession of the
 * `RGV-` code is the authority. Returns nothing that can redeem the invitation:
 * the `INV-` code is on this device's screen and nowhere else.
 */
export async function createInvitation(req: CreateInvitationRequest): Promise<CreateInvitationResponse> {
    return api.post('/api/invitation', req);
}

/**
 * What kind of invitation a lookup id names. Draws no CID and changes nothing —
 * unlike `beginInvitation`, which spends a delegate name out of the pool.
 * Rate-limited per IP on the node (skipped in Development).
 */
export async function inspectInvitation(lookupId: string, base?: string): Promise<InspectInvitationResponse> {
    // `base`: the node the invitation lives at — a delegate invitation's
    // organization origin (M7a). Without one, the entry node. Anonymous either
    // way: the holder of a code needs no session.
    return api.post('/api/invitation/inspect', { lookupId },
        base === undefined ? { anonymous: true } : { base, anonymous: true });
}
